import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { adminPaymentsApi } from '../api/adminApi';
import { LoadingStatus, PaymentTransaction, PaymentStats } from '../../types';

interface AdminPaymentsState {
  transactions: PaymentTransaction[];
  stats: PaymentStats | null;
  total: number;
  status: LoadingStatus;
  error: string | null;
}

const initialState: AdminPaymentsState = {
  transactions: [],
  stats: null,
  total: 0,
  status: 'idle',
  error: null,
};

export const fetchPaymentTransactions = createAsyncThunk(
  'adminPayments/fetchTransactions',
  async (params: Record<string, unknown> | undefined, { rejectWithValue }) => {
    try {
      const res = await adminPaymentsApi.list(params);
      return res.data as { items: PaymentTransaction[]; total: number; page: number; pages: number };
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch transactions');
    }
  },
);

export const fetchPaymentStats = createAsyncThunk(
  'adminPayments/fetchStats',
  async (_, { rejectWithValue }) => {
    try {
      const res = await adminPaymentsApi.getStats();
      return res.data as PaymentStats;
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      return rejectWithValue(e.response?.data?.message || 'Failed to fetch stats');
    }
  },
);

const adminPaymentsSlice = createSlice({
  name: 'adminPayments',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentTransactions.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchPaymentTransactions.fulfilled, (state, { payload }) => {
        state.transactions = payload.items ?? [];
        state.total = payload.total ?? 0;
        state.status = 'succeeded';
      })
      .addCase(fetchPaymentTransactions.rejected, (state, { payload }) => {
        state.error = payload as string;
        state.status = 'failed';
      })
      .addCase(fetchPaymentStats.fulfilled, (state, { payload }) => {
        state.stats = payload;
      });
  },
});

export default adminPaymentsSlice.reducer;

export const selectPaymentTransactions = (state: { adminPayments: AdminPaymentsState }) =>
  state.adminPayments.transactions;
export const selectPaymentStats = (state: { adminPayments: AdminPaymentsState }) =>
  state.adminPayments.stats;
export const selectPaymentsStatus = (state: { adminPayments: AdminPaymentsState }) =>
  state.adminPayments.status;
export const selectPaymentsTotal = (state: { adminPayments: AdminPaymentsState }) =>
  state.adminPayments.total;
